// Helpers om netwerkfouten in de add-in begrijpelijker te maken.
//
// Een bare `fetch()` gooit alleen een TypeError ("Failed to fetch" / "Load failed" /
// "NetworkError") wanneer de aanvraag de server nóóit bereikt: geen internet, een
// blokkerende firewall/proxy of een TLS-/certificaatprobleem (vaak bij bedrijfs- en
// zorgnetwerken die HTTPS-verkeer inspecteren). Die situatie is voor een gebruiker
// niet te onderscheiden van een echte serverfout zonder extra context, dus voegen we
// die context hier toe.
//
// Belangrijk: bij zo'n netwerk-/CORS-fout (bijv. een nginx 413 zonder CORS-header)
// kan de browser de response niet lezen, dus de API logt niets én de frontend zag
// het voorheen ook niet. Daarom rapporteren we deze fouten hier expliciet aan Sentry.

import * as Sentry from "@sentry/react";

const DEFAULT_TIMEOUT_MS = 20_000;

/** Hostnaam uit een URL halen voor in de foutmelding, met veilige fallback. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** True als de fout een netwerk-/verbindingsfout is (request bereikte de server niet). */
function isNetworkError(e: unknown): boolean {
  if (e instanceof TypeError) return true;
  const msg = (e as Error)?.message ?? "";
  return /failed to fetch|load failed|networkerror|network request failed/i.test(msg);
}

/**
 * Vertaalt een fetch-fout naar een begrijpelijke Nederlandse melding.
 * `url` is de aangeroepen endpoint, zodat we de juiste host kunnen tonen.
 */
export function describeFetchError(e: unknown, url: string): string {
  const host = hostOf(url);

  if ((e as Error)?.name === "AbortError") {
    return `De Dibretto-server (${host}) reageert niet binnen de tijd. Controleer je internetverbinding of probeer het later opnieuw. Op een bedrijfs- of zorgnetwerk kan een proxy het verkeer blokkeren — vraag je IT-beheerder om *.dibretto.com toe te staan.`;
  }

  if (isNetworkError(e)) {
    return `Geen verbinding met de Dibretto-server (${host}). Controleer je internetverbinding en probeer het opnieuw. Blijft het misgaan op een bedrijfs- of zorgnetwerk, dan blokkeert een firewall mogelijk *.dibretto.com — vraag dit na bij je IT-beheerder.`;
  }

  return (e as Error)?.message || "Onbekende fout";
}

/**
 * Rapporteert een fetch-fout aan Sentry en geeft de leesbare melding terug, zodat
 * een mislukte API-call nooit meer onzichtbaar is. `extra` kan diagnostische context
 * bevatten (bv. de bestandsgrootte van de upload) om snel een 413/size-limiet te
 * herkennen. Veilig in dev: Sentry is daar niet geïnitialiseerd en doet dan niets.
 *
 * Geeft de Nederlandse melding van describeFetchError() terug.
 */
export function reportFetchError(
  e: unknown,
  url: string,
  extra: Record<string, unknown> = {}
): string {
  Sentry.captureException(e, {
    tags: { area: "add-in-fetch" },
    extra: { url, ...extra },
  });
  return describeFetchError(e, url);
}

/**
 * fetch met time-out. Voorkomt dat de add-in eindeloos blijft hangen wanneer een
 * proxy de verbinding accepteert maar nooit antwoordt.
 */
export function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
  timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
}
